import { tv } from "tailwind-variants";
import {
  RiCheckboxCircleFill,
  RiProgress3Line,
  RiSettings2Line,
} from "@remixicon/react";
import { AlertBadge } from "@bdo/cornerstone-ui";
import type { WorkflowStepInstance } from "@/types";
import { STEP_STATUS, SUB_STATUS } from "./constants";
import { useMemo } from "react";

function getHeadingStatus(
  steps: WorkflowStepInstance[],
): (typeof STEP_STATUS)[keyof typeof STEP_STATUS] {
  if (steps.some((step) => step.status === STEP_STATUS.IN_PROGRESS)) {
    return STEP_STATUS.IN_PROGRESS;
  }
  if (steps.every((step) => step.status === STEP_STATUS.COMPLETED)) {
    return STEP_STATUS.COMPLETED;
  }
  return STEP_STATUS.PENDING;
}

interface WorkflowSidebarSectionTitleProps {
  heading: string;
  steps: WorkflowStepInstance[];
}

const styles = tv({
  slots: {
    sectionTitle: "text-lg flex gap-sm items-center",
  },
  variants: {
    status: {
      Completed: {
        sectionTitle: "text-success-dark",
      },
      InProgress: {
        sectionTitle: "text-mono-darker",
      },
      Pending: {
        sectionTitle: "text-mono",
      },
      // Deliberately included without styles for TypeScript
      AwaitingUser: {},
      Failed: {},
      Cancelled: {},
    },
  },
});

export function WorkflowSidebarSectionTitle({
  heading,
  steps,
}: Readonly<WorkflowSidebarSectionTitleProps>) {
  const { sectionTitle: sectionTitleStyle } = styles();

  const headingStatus = useMemo(() => getHeadingStatus(steps), [steps]);

  const hasAttentionSubStatus = useMemo(
    () =>
      steps.some(
        (step) =>
          step.subStatus === SUB_STATUS.AWAITING_OUTPUTS_APPROVAL ||
          step.subStatus === SUB_STATUS.REQUIRES_ADDITIONAL_INFO,
      ),
    [steps],
  );

  const hasAwaitingEngineSubStatus = useMemo(
    () =>
      steps.some(
        (step) => step.subStatus === SUB_STATUS.AWAITING_WORKFLOW_ENGINE,
      ),
    [steps],
  );

  return (
    <span
      className={sectionTitleStyle({ status: headingStatus })}
      data-testid={`workflow-heading-${heading}`}
    >
      {headingStatus === STEP_STATUS.COMPLETED && (
        <RiCheckboxCircleFill
          data-testid={`heading-completed-icon-${heading}`}
        />
      )}
      {hasAwaitingEngineSubStatus &&
        headingStatus === STEP_STATUS.IN_PROGRESS && (
          <RiSettings2Line
            className="text-xl animate-spin [animation-duration:5s]"
            data-testid={`heading-in-progress-icon-${heading}`}
          />
        )}
      {hasAttentionSubStatus && headingStatus === STEP_STATUS.IN_PROGRESS && (
        <div className="ml-[3px]">
          <AlertBadge data-testid={`heading-attention-icon-${heading}`} />
        </div>
      )}
      {headingStatus === STEP_STATUS.PENDING && (
        <RiProgress3Line
          className="text-xl"
          data-testid={`heading-pending-icon-${heading}`}
        />
      )}
      <span
        title={heading}
        className="overflow-hidden text-ellipsis whitespace-nowrap"
      >
        {heading}
      </span>
    </span>
  );
}
